import Cookies from 'js-cookie';
import React, { useState, useEffect, PropsWithChildren } from 'react';

import {
    HeaderServiceConfiguration,
    fetchHeaderContent,
    isHeaderContent,
} from './api/headerServiceClient';
import { HeaderContent, HeaderContentImpl } from './api/types';
import AppContext from './context';
import { AppContextType, defaultContext } from './context/AppContext';
import { useCart } from './hooks/useCart';
import { useLogin } from './hooks/useLogin';
import { initializeSentry, withSentryHub } from './utils/sentry';

export type HeaderContextProps = {
    content?: HeaderContent;
    configuration?: HeaderServiceConfiguration;
};

function HeaderContextProvider({
    children,
    content,
    configuration,
}: PropsWithChildren<HeaderContextProps>): JSX.Element {
    const [selectedShopLanguage, setSelectedShopLanguage] = useState<
        string | undefined
    >(undefined);

    const [isLoggedIn, customerId, customerStatusLoaded] = useLogin();
    const [cart] = useCart();

    useEffect(() => {
        const selectedShopLanguageVal = Cookies.get('_zpLanguage');
        setSelectedShopLanguage(selectedShopLanguageVal);
    }, []);

    const [headerContentState, setHeaderContentState] = useState<
        HeaderContentImpl | undefined
    >(() => (isHeaderContent(content) ? content : undefined));

    const [sentryInit, setSentryInit] = useState(false);
    useEffect(() => {
        if (sentryInit === false) {
            initializeSentry();
            setSentryInit(true);
            if (!content && configuration) {
                fetchHeaderContent(configuration)
                    .then((result) => {
                        if (isHeaderContent(result)) {
                            setHeaderContentState(result);
                        }
                    })
                    .catch((e) => withSentryHub()?.captureException(e));
            }
        }
    }, [content, configuration, sentryInit]);

    const context: AppContextType =
        headerContentState && isHeaderContent(headerContentState)
            ? {
                  ...headerContentState,
                  categories: headerContentState.categories ?? [],
                  translations: new Map(
                      Object.entries(headerContentState.translations ?? {}),
                  ),
                  cart,
                  isLoggedIn,
                  selectedShopLanguage,
                  customerId,
                  customerStatusLoaded,
                  isError: false,
                  bundle: headerContentState.bundle,
                  backendHeaderVersion: headerContentState.headerVersion,
              }
            : { ...defaultContext, isError: true };
    return (
        <AppContext.Provider value={context}>{children}</AppContext.Provider>
    );
}

export default HeaderContextProvider;
